import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, BookOpen, Calendar, FileText, GraduationCap, ClipboardList, Settings,
  LogOut, Clock, Video, RefreshCw, X, Check, Send, ExternalLink, CheckCircle,
  Trophy, TrendingUp, Star, User, Sparkles, Target, MessageSquare,
  CalendarDays, Camera, Globe, MessageCircle, ChevronRight, Plus, ChevronLeft, Mail,
  XCircle, AlertTriangle, Link2, Trash2, Menu, CreditCard, Award, Heart, BarChart3, Code as CodeIcon, Bot, Folder
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';
import ImpersonationBanner from '../components/ImpersonationBanner';
import OnboardingWizard from '../components/OnboardingWizard';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import StudentHomeworkSection from '../components/StudentHomeworkSection';
import StudentCodingProjects from '../components/StudentCodingProjects';
import RightUtilityRail from '../components/RightUtilityRail';

import { getTopicImage, getTopicCategory, getTopicColor } from '../utils/topicImages';
import LearningPath from '../components/LearningPath';
import LeaderboardSidebar from '../components/LeaderboardSidebar';
import StudentSchedulerPanel from '../components/StudentSchedulerPanel';
import NotificationBell from '../components/NotificationBell';
import MyFilesPage from '../components/MyFilesPage';
import ReceiptsPanel from '../components/ReceiptsPanel';
import StudentReportCardArchive from '../components/StudentReportCardArchive';
import AIBuddyPanel from '../components/AIBuddyPanel';
import ParentMetricsRow from '../components/ParentMetricsRow';
import FeedbackTimeline from '../components/FeedbackTimeline';
import ProgressBadges from '../components/ProgressBadges';
import MathOlympiadPanel from '../components/MathOlympiadPanel';
import StudentXPBar from '../components/StudentXPBar';
import StudentCertificates from '../components/StudentCertificates';
import VerticalSelector from '../components/VerticalSelector';

const TIMEZONE_MAP = {
  'IST': 'Asia/Kolkata', 'EST': 'America/New_York', 'PST': 'America/Los_Angeles',
  'GMT': 'Europe/London', 'SGT': 'Asia/Singapore', 'CST': 'America/Chicago',
  'CET': 'Europe/Paris', 'AEST': 'Australia/Sydney',
};

const WEEKDAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

const RATING_QUESTIONS = [
  { key: 'explanation_quality', label: 'How well did the teacher explain?' },
  { key: 'class_engagement', label: 'How engaging was the class?' },
  { key: 'learning_new', label: 'Did you learn something new?' },
  { key: 'pace_comfort', label: 'Was the pace comfortable?' },
  { key: 'recommend_teacher', label: 'Would you recommend this teacher?' },
];

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const fileInputRef = useRef(null);

  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const [aiBuddyOpen, setAiBuddyOpen] = useState(false);
  const [olympiadOpen, setOlympiadOpen] = useState(false);
  const [olympiadActive, setOlympiadActive] = useState(null);

  // Data
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [upcomingClass, setUpcomingClass] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [badgeData, setBadgeData] = useState(null);

  // Timezone
  const [studentTimezone, setStudentTimezone] = useState('IST');
  const [localTime, setLocalTime] = useState('');

  // Schedule
  const [studentSchedule, setStudentSchedule] = useState([]);
  const [classAllocation, setClassAllocation] = useState({ total_assigned: 0, completed: 0, remaining: 0 });

  // Reschedule
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleNewDate, setRescheduleNewDate] = useState('');
  const [rescheduleNewTime, setRescheduleNewTime] = useState('');
  const [teacherAvailForReschedule, setTeacherAvailForReschedule] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);

  // Cancel
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  // Active vertical — used to scope XP/progress display across the dashboard
  const [activeVertical, setActiveVertical] = useState('');

  // Add Class
  const [addClassOpen, setAddClassOpen] = useState(false);
  const [addClassDate, setAddClassDate] = useState('');
  const [addClassTime, setAddClassTime] = useState('');
  const [addClassTeacher, setAddClassTeacher] = useState('');

  // Recurring Schedule
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [changeRecurringOpen, setChangeRecurringOpen] = useState(false);
  const [changeRecurringTarget, setChangeRecurringTarget] = useState(null);
  const [newRecurringDays, setNewRecurringDays] = useState([]);
  const [newRecurringTime, setNewRecurringTime] = useState('');
  const [recurringReason, setRecurringReason] = useState('');

  // Class Rating
  const [completedClasses, setCompletedClasses] = useState([]);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null);
  const [classRatings, setClassRatings] = useState({ explanation_quality: 0, class_engagement: 0, learning_new: 0, pace_comfort: 0, recommend_teacher: 0 });
  const [submittingRating, setSubmittingRating] = useState(false);

  // Class Credits & Payment
  const [classCredits, setClassCredits] = useState({ total_purchased: 0, classes_completed: 0, classes_remaining: 0 });
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Settings
  const [profilePicture, setProfilePicture] = useState(null);
  const [settingsTimezone, setSettingsTimezone] = useState('IST');
  const [settingsGrade, setSettingsGrade] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [recordingDisabled, setRecordingDisabled] = useState(false);
  const [savingRecordingPref, setSavingRecordingPref] = useState(false);

  // Discussions
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMessage, setNewMessage] = useState('');

  // Report Cards
  const [reportCardsData, setReportCardsData] = useState([]);
  const [viewingReportCard, setViewingReportCard] = useState(null);

  // Learning Zone
  const [learningLinks, setLearningLinks] = useState([]);

  // Parent Hub (inside Student Dashboard)
  const [parentHubData, setParentHubData] = useState(null);
  const [parentHubLoading, setParentHubLoading] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  const [parentInvoices, setParentInvoices] = useState([]);
  const [buyClassesOpen, setBuyClassesOpen] = useState(false);
  const [buyNumClasses, setBuyNumClasses] = useState(8);

  // Progress Report
  const [progressReport, setProgressReport] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);

  // Class Entries (from teacher class management)
  const [classEntries, setClassEntries] = useState([]);
  const [classViewTab, setClassViewTab] = useState('upcoming'); // 'upcoming' | 'completed'

  // Right sidebar calendar - use month view
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [scheduleMonth, setScheduleMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const getCurrentTimeInTZ = (tz) => {
    try { return new Date().toLocaleTimeString('en-US', { timeZone: TIMEZONE_MAP[tz] || tz, hour: '2-digit', minute: '2-digit', hour12: true }); } catch (err) { return ''; }
  };
  const convertTime = (time24, fromTZ, toTZ) => {
    try {
      if (!time24 || !fromTZ) return time24;
      const [h, m] = time24.split(':').map(Number);
      const _now = new Date(); const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
      const fromIANA = TIMEZONE_MAP[fromTZ] || fromTZ;
      const toIANA = TIMEZONE_MAP[toTZ] || toTZ;

      // Create a UTC date, then find what offset the source timezone has
      const utcDate = new Date(`${today}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00Z`);
      // What time is utcDate in the source timezone?
      const inSource = utcDate.toLocaleString('en-US', { timeZone: fromIANA, hour: 'numeric', minute: '2-digit', hour12: false });
      const srcParts = inSource.match(/(\d+):(\d+)/);
      if (!srcParts) return time24;
      const srcH = parseInt(srcParts[1]) % 24;
      const srcM = parseInt(srcParts[2]);
      // How many minutes off is the source display from what we want?
      const diffMs = ((h - srcH) * 60 + (m - srcM)) * 60000;
      const adjustedDate = new Date(utcDate.getTime() + diffMs);

      return adjustedDate.toLocaleTimeString('en-US', {
        timeZone: toIANA, hour: '2-digit', minute: '2-digit', hour12: true
      });
    } catch (err) { return time24; }
  };

  // Join is enabled 1 minute before class start time and during class (until marked completed)
  const isJoinEnabled = (dateStr, timeStr) => {
    try {
      if (!dateStr || !timeStr) return false;
      const classDateTime = new Date(`${dateStr}T${timeStr}:00`);
      const now = new Date();
      const diffMs = classDateTime - now;
      // PLATFORM RULE: link active 10 minutes BEFORE class start, expires 20 minutes AFTER start
      return diffMs <= 10 * 60 * 1000 && diffMs > -20 * 60 * 1000;
    } catch { return false; }
  };

  // Define fetchData BEFORE the useEffect that uses it
  const fetchData = useCallback(async () => {
    try {
      const [coursesRes, enrollmentsRes, scheduleRes, asgnsRes, currRes] = await Promise.all([
        api.get('/courses'), api.get('/enrollments'),
        api.get('/student/schedule').catch(() => ({ data: [] })),
        api.get('/assignments').catch(() => ({ data: [] })),
        api.get('/my-curriculum').catch(() => ({ data: [] }))
      ]);
      setCourses(coursesRes.data); setEnrollments(enrollmentsRes.data);
      setAssignments(asgnsRes.data || []);
      setCurricula(currRes.data || []);
      const allClasses = scheduleRes.data || [];
      setStudentSchedule(allClasses);
      const todayStr = new Date().toISOString().slice(0, 10);
      const upcoming = allClasses.filter(b => b.status === 'scheduled' || b.status === 'rescheduled').sort((a, b) => new Date(`${a.date}T${a.time || '00:00'}:00`) - new Date(`${b.date}T${b.time || '00:00'}:00`)).find(b => b.date >= todayStr);
      setUpcomingClass(upcoming || null);
    } catch (err) {  toast.error('Failed to load data'); }
    try { const aRes = await api.get('/student/class-allocation'); setClassAllocation(aRes.data); } catch (err) {  }
    try { const rRes = await api.get('/student/recurring-schedules'); setRecurringSchedules(rRes.data); } catch (err) {  }
    try { const ceRes = await api.get('/student/class-entries'); setClassEntries(ceRes.data || []); } catch (err) {  }
    try { const credRes = await api.get('/student/class-credits'); setClassCredits(credRes.data || { total_purchased: 0, classes_completed: 0, classes_remaining: 0 }); } catch (err) {  }
    try { const phRes = await api.get('/student/payment-history'); setPaymentHistory(phRes.data || []); } catch (err) {  }
    // Also load parent hub data for dashboard metrics
    try { const hubRes = await api.get(`/parent/hub/${user?.id}`); setParentHubData(hubRes.data); } catch (err) {  }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'student') { navigate('/login'); return; }
    fetchData();
  }, [user, navigate, fetchData]);

  useEffect(() => {
    if (upcomingClass) {
      const interval = setInterval(() => {
        const now = new Date();
        const classTime = new Date(`${upcomingClass.date}T${upcomingClass.time || upcomingClass.start_time || '00:00'}:00`);
        const diff = classTime - now;
        if (diff > 0) setTimeRemaining({ hours: Math.floor(diff / 3600000), minutes: Math.floor((diff % 3600000) / 60000), seconds: Math.floor((diff % 60000) / 1000), total: diff });
        else setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, total: 0 });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [upcomingClass]);

  useEffect(() => {
    api.get('/students/timezone').then(r => {
      const tz = r.data?.timezone || 'IST';
      setStudentTimezone(tz); setSettingsTimezone(tz);
    }).catch(() => {});
    api.get('/student/profile').then(r => {
      if (r.data?.profile_picture) setProfilePicture(r.data.profile_picture);
      if (r.data?.grade) setSettingsGrade(r.data.grade);
    }).catch(() => {});
    api.get('/user/preferences/recording').then(r => {
      setRecordingDisabled(!!r.data?.recording_disabled);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => setLocalTime(new Date().toLocaleString('en-US', {
      timeZone: TIMEZONE_MAP[studentTimezone] || studentTimezone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, weekday: 'short', month: 'short', day: 'numeric'
    }));
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [studentTimezone]);

  const fetchLearningLinks = useCallback(async () => { try { const res = await api.get('/students/learning-links'); setLearningLinks(res.data || []); } catch (err) {  } }, []);
  const fetchBadges = useCallback(async () => { try { const res = await api.get('/student/badges'); setBadgeData(res.data); } catch (err) {  } }, []);
  const fetchReportCards = useCallback(async () => { try { const res = await api.get('/student/report-cards'); setReportCardsData(res.data); } catch (err) {  } }, []);
  const fetchClassEntries = useCallback(async () => { try { const res = await api.get('/student/class-entries'); setClassEntries(res.data || []); } catch (err) {  } }, []);
  const fetchStudentSchedule = useCallback(async () => {
    try { const res = await api.get('/student/schedule'); setStudentSchedule(res.data); } catch (err) {  }
    try { const aRes = await api.get('/student/class-allocation'); setClassAllocation(aRes.data); } catch (err) {  }
    try { const rRes = await api.get('/student/recurring-schedules'); setRecurringSchedules(rRes.data); } catch (err) {  }
  }, []);
  const fetchCompletedClasses = useCallback(async () => { try { const res = await api.get('/student/completed-classes'); setCompletedClasses(res.data); } catch (err) {  } }, []);
  const fetchThreads = useCallback(async () => { try { const res = await api.get('/discussion-threads/mine'); setThreads(res.data); } catch (err) {  } }, []);
  const fetchOlympiad = useCallback(async () => {
    try { const res = await api.get('/math-olympiad/active'); setOlympiadActive(res.data || null); } catch { setOlympiadActive(null); }
  }, []);

  useEffect(() => { fetchOlympiad(); }, [fetchOlympiad]);

  useEffect(() => {
    if (activeSection === 'schedule') { fetchStudentSchedule(); }
    if (activeSection === 'rate-class') fetchCompletedClasses();
    if (activeSection === 'discussions') fetchThreads();
    if (activeSection === 'report-card') fetchReportCards();
    if (activeSection === 'learning-zone') fetchLearningLinks();
    if (activeSection === 'badges') fetchBadges();
    if (activeSection === 'dashboard') { fetchParentHub(); fetchCompletedClasses(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, fetchStudentSchedule, fetchCompletedClasses, fetchThreads, fetchReportCards, fetchLearningLinks, fetchBadges]);

  const fetchParentHub = async () => {
    setParentHubLoading(true);
    try {
      const res = await api.get(`/parent/hub/${user?.id}`);
      setParentHubData(res.data);
    } catch (err) {  setParentHubData(null); }
    setParentHubLoading(false);
    try { const invRes = await api.get('/invoices'); setParentInvoices(invRes.data); } catch (err) {  }
  };
  const fetchProgressReport = async () => {
    setProgressLoading(true);
    try { const res = await api.get('/student/progress-report'); setProgressReport(res.data); } catch (err) {  setProgressReport(null); }
    setProgressLoading(false);
  };
  const handleBuyClasses = async () => {
    if (buyNumClasses <= 0) { toast.error('Select number of classes'); return; }
    try {
      await api.post('/student/buy-classes', { student_id: user?.id, num_classes: buyNumClasses });
      toast.success('Class request submitted! Admin will generate an invoice.');
      setBuyClassesOpen(false);
      setBuyNumClasses(8);
      fetchParentHub();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to submit request'); }
  };
  const handleCancelClass = async () => {
    // Enforce 1-hour rule on frontend
    const classDate = cancelTarget.date;
    const classTime = cancelTarget.time || cancelTarget.start_time || '00:00';
    if (classDate && classTime) {
      const classDateTime = new Date(`${classDate}T${classTime}:00`);
      const now = new Date();
      const diffMin = (classDateTime - now) / 60000;
      if (diffMin < 60) {
        toast.error('Cannot cancel class within 1 hour of start time');
        setCancelDialogOpen(false);
        setCancelTarget(null);
        return;
      }
    }
    try {
      await api.post(`/student/schedule/${cancelTarget.id}/cancel`);
      toast.success('Class cancelled');
      setCancelDialogOpen(false); setCancelTarget(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  // Reschedule class (auto if teacher available)
  const handleRescheduleClass = async () => {
    if (!rescheduleTarget || !rescheduleNewDate || !rescheduleNewTime) { toast.error('Select date and time'); return; }
    try {
      await api.post(`/student/schedule/${rescheduleTarget.id}/reschedule`, { new_date: rescheduleNewDate, new_time: rescheduleNewTime });
      toast.success('Class rescheduled!');
      setRescheduleDialogOpen(false); setRescheduleTarget(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Teacher not available at this time'); }
  };

  const openRescheduleDialog = async (cls) => {
    setRescheduleTarget(cls);
    setRescheduleNewDate(''); setRescheduleNewTime('');
    setAvailableSlots([]);
    // PLATFORM RULE: students may reschedule up to 6 hours before class start
    const classDate = cls.date;
    const classTime = cls.time || cls.start_time || '00:00';
    if (classDate && classTime) {
      const classDateTime = new Date(`${classDate}T${classTime}:00`);
      const now = new Date();
      const diffMin = (classDateTime - now) / 60000;
      if (diffMin < 360) {
        toast.error('Reschedule allowed only up to 6 hours before class start');
        return;
      }
    }
    try {
      const res = await api.get(`/student/available-slots/${cls.teacher_id}`);
      setAvailableSlots(res.data?.slots || []);
      setTeacherAvailForReschedule(res.data);
    } catch {
      try {
        const res = await api.get(`/student/teacher-availability/${cls.teacher_id}`);
        setTeacherAvailForReschedule(res.data);
      } catch (err) {  setTeacherAvailForReschedule(null); }
    }
    setRescheduleDialogOpen(true);
  };

  // Add class
  const handleAddClass = async () => {
    if (!addClassDate || !addClassTime) { toast.error('Select date and time'); return; }
    const teacherId = addClassTeacher || studentSchedule.find(s => s.teacher_id)?.teacher_id;
    if (!teacherId) { toast.error('No teacher assigned'); return; }
    try {
      await api.post('/student/add-class', { teacher_id: teacherId, date: addClassDate, time: addClassTime, timezone: studentTimezone });
      toast.success('Class added!');
      setAddClassOpen(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add class'); }
  };

  // Change recurring schedule
  const handleChangeRecurring = async () => {
    if (!changeRecurringTarget) return;
    try {
      await api.post('/student/recurring-schedule-change', {
        schedule_id: changeRecurringTarget.id,
        new_days_of_week: newRecurringDays,
        new_time: newRecurringTime,
        reason: recurringReason
      });
      toast.success('Change request submitted for admin approval!');
      setChangeRecurringOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSubmitRating = async () => {
    if (Object.values(classRatings).some(v => v === 0)) { toast.error('Please rate all questions'); return; }
    setSubmittingRating(true);
    try {
      await api.post('/student/class-rating', { class_id: ratingTarget.id, ratings: classRatings });
      toast.success('Rating submitted!');
      setRatingDialogOpen(false);
      setClassRatings({ explanation_quality: 0, class_engagement: 0, learning_new: 0, pace_comfort: 0, recommend_teacher: 0 });
      fetchCompletedClasses();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmittingRating(false);
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try { const res = await api.post('/student/profile-picture', formData, { headers: { 'Content-Type': 'multipart/form-data' } }); setProfilePicture(res.data.profile_picture); toast.success('Updated!'); } catch (err) {  toast.error('Failed'); }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try { await api.put('/student/settings', { timezone: settingsTimezone, grade: settingsGrade }); setStudentTimezone(settingsTimezone); toast.success('Settings saved!'); } catch (err) {  toast.error('Failed'); }
    setSavingSettings(false);
  };

  const handleToggleRecording = async (newVal) => {
    setSavingRecordingPref(true);
    try {
      await api.put('/user/preferences/recording', { disabled: newVal });
      setRecordingDisabled(newVal);
      toast.success(newVal ? 'Recordings disabled' : 'Recordings enabled');
    } catch (err) {
      toast.error('Failed to update preference');
    }
    setSavingRecordingPref(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;
    try {
      await api.post(`/teacher/discussion-threads/${selectedThread.id}/messages`, { message: newMessage });
      setNewMessage('');
      const res = await api.get(`/discussion-threads/${selectedThread.id}`);
      setSelectedThread(res.data); fetchThreads();
    } catch (err) {  toast.error('Failed'); }
  };

  const handleLogout = () => { logout(); navigate('/'); };
  const handleJoinClass = async (cls) => {
    const classToJoin = cls || upcomingClass;
    if (!classToJoin?.id) { toast.error('No class selected'); return; }
    try {
      const res = await api.post(`/class/${classToJoin.id}/join`);
      const link = res.data.meeting_link;
      if (link) {
        window.open(link, '_blank');
        toast.success('Joined class — duration tracking started');
      } else {
        toast.info('Meeting link will be shared before class');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to join class';
      toast.error(msg);
    }
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Calendar helpers - month view
  const getCalMonthDays = () => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  };
  const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const shiftCalMonth = (dir) => setCalMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + dir, 1));

  const now = new Date();
  const upcomingScheduledClasses = studentSchedule
    .filter(c => (c.status === 'scheduled' || c.status === 'rescheduled') && new Date(`${c.date}T${c.time||'00:00'}:00`) >= now)
    .sort((a,b) => new Date(`${a.date}T${a.time||'00:00'}`) - new Date(`${b.date}T${b.time||'00:00'}`));

  // Month options for schedule filter
  const scheduleMonths = [...new Set(studentSchedule.filter(c => c.status !== 'cancelled').map(c => c.date?.slice(0, 7)))].sort();

  const filteredSchedule = studentSchedule
    .filter(cls => cls.status !== 'cancelled' && cls.status !== 'completed')
    .filter(cls => scheduleMonth === 'all' || !scheduleMonth || cls.date?.startsWith(scheduleMonth))
    .sort((a, b) => new Date(`${a.date}T${a.time||'00:00'}`) - new Date(`${b.date}T${b.time||'00:00'}`))
    .slice(0, 5);

  const sidebarItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, color: 'text-blue-600 bg-blue-100' },
    { id: 'learning-path', label: 'My Learning', icon: Target, color: 'text-indigo-600 bg-indigo-100' },
    { id: 'schedule', label: 'Schedule', icon: Calendar, color: 'text-violet-600 bg-violet-100' },
    { id: 'homework', label: 'Homework', icon: BookOpen, color: 'text-blue-700 font-bold bg-blue-100' },
    { id: 'coding-projects', label: 'My Projects', icon: CodeIcon, color: 'text-violet-700 font-bold bg-violet-100' },
    { id: 'learning-zone', label: 'Learning Zone', icon: Link2, color: 'text-teal-600 bg-teal-100' },
    { id: 'report-card', label: 'Report Card', icon: FileText, color: 'text-indigo-600 bg-indigo-100' },
    { id: 'feedback', label: 'Class Feedback', icon: ClipboardList, color: 'text-orange-600 bg-orange-100' },
    { id: 'discussions', label: 'Discussions', icon: MessageCircle, color: 'text-pink-600 bg-pink-100' },
    { id: 'badges', label: 'Achievements', icon: Award, color: 'text-amber-600 bg-amber-100' },
    { id: 'my-files', label: 'My Files', icon: Folder, color: 'text-emerald-600 bg-emerald-100' },
    { id: 'settings', label: 'Settings', icon: Settings, color: 'text-slate-600 font-semibold bg-slate-100' },
  ];

  const handleSidebarClick = (item) => {
    setActiveSection(item.id);
    setMobileSidebar(false);
    if (item.id === 'parent-hub') fetchParentHub();
    if (item.id === 'progress-report') fetchProgressReport();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-500/10"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="min-h-screen flex v2 v2-app" data-testid="student-dashboard">
      <ImpersonationBanner />
      <OnboardingWizard
        open={showOnboardingWizard}
        onClose={() => setShowOnboardingWizard(false)}
        mode="student"
        user={user}
        onComplete={() => { window.location.reload(); }}
      />

      {/* Mobile sidebar overlay */}
      {mobileSidebar && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileSidebar(false)} />}

      {/* Sidebar */}
      <aside className={`w-64 v2-sidebar min-h-screen flex-col fixed left-0 top-0 z-50 transition-transform duration-300 ${mobileSidebar ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex`} data-testid="student-sidebar">
        <div className="flex items-center justify-between mb-4 px-2">
          <div className="flex items-center gap-2.5">
            <img src="/assets/logo.png" alt="CodeITT" className="h-11 w-auto object-contain rounded-lg" data-testid="codeitt-logo" />
            <div>
              <span className="text-base font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', 'Manrope', sans-serif" }}>CodeITT</span>
              <p className="text-[10px] font-bold uppercase tracking-widest text-violet-600 -mt-0.5">Student Portal</p>
            </div>
          </div>
        </div>
        <div className="mb-4 px-2 flex items-center gap-3">
          <div className="relative">
            {profilePicture ? (
              <img src={`${API_BASE}${profilePicture}`} alt="Profile" className="w-10 h-10 rounded-full object-cover border-2 border-violet-300" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center"><User className="w-5 h-5 text-violet-600" /></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-extrabold text-slate-900 text-sm truncate">{user?.name}</h3>
            <Select value={studentTimezone} onValueChange={async (v) => {
              try {
                await api.put('/student/settings', { timezone: v });
                setStudentTimezone(v);
                setSettingsTimezone(v);
                toast.success(`Timezone set to ${v}`);
              } catch (err) {  toast.error('Failed to update timezone'); }
            }}>
              <SelectTrigger className="h-5 w-auto px-1.5 border-0 bg-transparent text-[11px] text-slate-500 font-medium p-0 gap-1 focus:ring-0 shadow-none hover:text-violet-600" data-testid="sidebar-tz-select">
                <Globe className="w-3 h-3 shrink-0" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['IST', 'EST', 'PST', 'CST', 'GMT', 'SGT', 'CET', 'AEST', 'JST', 'KST', 'MST', 'UTC'].map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <nav className="flex-1 flex flex-col gap-1 overflow-y-auto pr-1">
          {sidebarItems.filter(i => !i.isParentHub).map((item) => {
            const Icon = item.icon;
            const active = activeSection === item.id;
            return (
              <button key={item.id} onClick={() => handleSidebarClick(item)} data-testid={`sidebar-${item.id}`}
                className={`v2-nav-item w-full text-left ${active ? 'active' : ''}`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>
        {/* Parent Hub Button */}
        <div className="pt-3 mt-3 border-t border-slate-200/60">
          <button onClick={() => handleSidebarClick({ id: 'parent-hub' })}
            className={`w-full flex items-center justify-center gap-2.5 px-3 py-3 rounded-xl text-sm font-bold transition-all border-2
              ${activeSection === 'parent-hub'
                ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-transparent shadow-lg shadow-violet-500/30'
                : 'bg-white text-slate-900 border-slate-200 hover:border-violet-300 hover:shadow-md'
              }`}
            data-testid="sidebar-parent-hub">
            <Heart className={`w-4 h-4 ${activeSection === 'parent-hub' ? 'text-white' : 'text-rose-500'}`} />
            Parent Hub
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:mr-80 flex flex-col min-h-screen min-w-0">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 glass-topbar sticky top-0 z-30">
          <button onClick={() => setMobileSidebar(true)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <span className="font-bold text-slate-800 text-base">CodeITT</span>
          <button onClick={handleLogout} className="p-2 hover:bg-red-50 rounded-lg" data-testid="mobile-logout-btn">
            <LogOut className="w-5 h-5 text-red-500" />
          </button>
        </div>
        {/* Desktop Top Bar */}
        <header className="hidden lg:flex sticky top-0 z-20 glass-topbar px-8 py-2.5 items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500 font-semibold">{localTime}</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell role="student" />
            <button onClick={handleLogout} className="flex items-center gap-2 px-5 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold text-sm transition-all shadow-sm" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </header>

        <div className="flex flex-1">
        {/* Center Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0 overflow-x-hidden admin-content">
          <AnnouncementsBanner />
          {/* Dashboard */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6 animate-fade-in">
              <div className="ds-page-hero animate-fade-in-up" data-testid="student-page-hero">
                <h1 data-testid="dashboard-welcome">Welcome, {user?.name}</h1>
                <VerticalSelector onChange={(v) => { setActiveVertical(v); fetchData(); }} />
                <p>Your learning journey continues here</p>
              </div>

              {/* Profile completion nudge (payment warning removed — admin approves receipts externally) */}
              {(() => {
                const missingProfile = !user?.imported_from_sheet && (!user?.grade || !user?.verticals || (Array.isArray(user?.verticals) && user.verticals.length === 0));
                if (!missingProfile) return null;
                return (
                  <div className="nb-card p-5 bg-amber-50 border-amber-300" data-testid="onboarding-nudge">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-900 text-base">Let's get you ready to learn</h3>
                        <ul className="text-sm text-slate-700 mt-1 space-y-1">
                          <li>• Complete your profile (grade + verticals)</li>
                        </ul>
                        <Button size="sm" className="nb-btn-primary mt-3" onClick={() => setShowOnboardingWizard(true)} data-testid="onboarding-start-btn">
                          Start onboarding
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Single centered column — no permanent right rail. */}
              <div className="space-y-6" data-testid="dashboard-main-column">
              <div data-testid="dashboard-right-panel" style={{ display: 'contents' }}>
              {/* Progress Metrics Row */}
              {(() => {
                const totalPurchased = parentHubData?.credits?.total_purchased || 0;
                const classesCompleted = parentHubData?.credits?.classes_completed || 0;
                const classesRemaining = parentHubData?.credits?.classes_remaining || 0;
                const attendancePct = parentHubData?.attendance_pct || 0;
                const streak = classesCompleted > 0 ? Math.min(classesCompleted, 7) : 0;
                const hasAnyData = totalPurchased > 0 || classesCompleted > 0;
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="student-progress-metrics">
                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-sm card-hover animate-fade-in-up stagger-1">
                      <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-2">
                        <div className="relative w-8 h-8">
                          <svg className="w-8 h-8 -rotate-90" viewBox="0 0 36 36">
                            <circle cx="18" cy="18" r="14" fill="none" stroke="#E2E8F0" strokeWidth="3" />
                            <circle cx="18" cy="18" r="14" fill="none" stroke="#3B82F6" strokeWidth="3" strokeDasharray={`${attendancePct * 0.88} 88`} strokeLinecap="round" className="animate-mini-ring" />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-blue-600">{attendancePct}%</span>
                        </div>
                      </div>
                      <p className="v2-num text-lg text-slate-900 animate-pop-in stagger-1">{hasAnyData ? `${attendancePct}%` : '—'}</p>
                      <p className="text-[11px] text-slate-500 font-medium">{hasAnyData ? 'Attendance' : 'Attendance · Start soon'}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-sm card-hover animate-fade-in-up stagger-2" data-testid="classes-progress-card">
                      {hasAnyData ? (
                        <>
                          <p className="v2-num text-2xl text-emerald-600 animate-pop-in stagger-2"><span data-testid="classes-completed-count">{classesCompleted}</span><span className="text-slate-400 text-lg">/{totalPurchased}</span></p>
                          <p className="text-[11px] text-slate-500 font-medium">Classes Completed</p>
                          <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                            <div className="bg-emerald-500 h-1.5 rounded-full animate-bar-grow" style={{ width: `${totalPurchased > 0 ? (classesCompleted / totalPurchased) * 100 : 0}%` }} />
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="v2-num text-lg text-slate-400"><span data-testid="classes-completed-count">0</span></p>
                          <p className="text-[11px] text-slate-500 font-medium">No classes yet</p>
                          <p className="text-[10px] text-slate-400 mt-1">Getting started</p>
                        </>
                      )}
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-sm card-hover animate-fade-in-up stagger-3">
                      <p className={`v2-num text-2xl animate-pop-in stagger-3 ${hasAnyData ? 'text-amber-600' : 'text-slate-400'}`}>{hasAnyData ? classesRemaining : '—'}</p>
                      <p className="text-[11px] text-slate-500 font-medium">{hasAnyData ? 'Remaining' : 'Start your journey'}</p>
                      {hasAnyData && <p className="text-[10px] text-slate-400 mt-1">of {totalPurchased} total</p>}
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-sm card-hover animate-fade-in-up stagger-4">
                      <p className={`v2-num text-2xl animate-pop-in stagger-4 ${streak > 0 ? 'text-violet-600' : 'text-slate-400'}`}>{streak > 0 ? `🔥 ${streak}` : '—'}</p>
                      <p className="text-[11px] text-slate-500 font-medium">{streak > 0 ? 'Day Streak' : 'No streak yet'}</p>
                      <div className="flex justify-center gap-0.5 mt-2">{[...Array(7)].map((_, i) => <div key={i} className={`w-2 h-2 rounded-full animate-dot ${i < streak ? 'bg-violet-500' : 'bg-slate-200'}`} style={{ animationDelay: `${0.3 + i * 0.06}s` }} />)}</div>
                    </div>
                  </div>
                );
              })()}

              {/* Upcoming/Completed Tabs */}
              <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit animate-fade-in-up stagger-2" data-testid="class-view-tabs">
                {['upcoming', 'completed'].map(tab => (
                  <button key={tab} onClick={() => setClassViewTab(tab)}
                    className={`px-5 py-2 rounded-lg text-xs font-bold transition-all ${classViewTab === tab ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                    data-testid={`tab-${tab}`}>
                    {tab === 'upcoming' ? 'Upcoming Classes' : 'Completed Classes'}
                  </button>
                ))}
              </div>

              {/* === UPCOMING CLASSES (up to 5) === */}
              {classViewTab === 'upcoming' && (() => {
                const now = new Date();
                const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                // Show classes from today onwards (by date, not by exact time — a class stays visible until marked completed)
                const scheduleUpcoming = studentSchedule
                  .filter(c => (c.status === 'scheduled' || c.status === 'rescheduled') && new Date(`${c.date}T23:59:59`) >= todayStart)
                  .map(c => ({ ...c, _source: 'schedule', _sortDate: new Date(`${c.date}T${c.time || c.start_time || '00:00'}:00`) }));
                const entryUpcoming = classEntries
                  .filter(e => e.status === 'incomplete' || e.status === 'carried_forward')
                  .map(e => ({ ...e, _source: 'entry', _sortDate: e.date ? new Date(`${e.date}T${e.time || '00:00'}:00`) : new Date('2099-01-01') }));
                const allUpcoming = [...scheduleUpcoming, ...entryUpcoming]
                  .sort((a, b) => a._sortDate - b._sortDate)
                  .slice(0, 5);

                return (
                  <div className="space-y-3" data-testid="upcoming-classes-section">
                    {allUpcoming.length === 0 ? (
                      <div className="nb-card p-8 text-center" data-testid="upcoming-empty-state">
                        <img src="https://static.prod-images.emergentagent.com/jobs/e95a3f19-61bf-4d6b-a843-30a42cd2cc29/images/36f64fd6d848be8a0ead97c137350894a1c33a07336de1454128edcd2ac341b3.png" alt="" className="w-24 h-24 mx-auto mb-3 object-contain" loading="lazy" />
                        <p className="text-slate-900 text-lg font-bold">🎉 You're all caught up!</p>
                        <p className="text-slate-500 text-sm mt-1">No upcoming classes are scheduled.</p>
                        <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
                          <Button size="sm" onClick={() => document.querySelector('[data-testid="dashboard-secondary-strip"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="bg-violet-600 hover:bg-violet-700 text-white font-bold h-9 px-4" data-testid="empty-book-btn">
                            <Calendar className="w-4 h-4 mr-1.5" /> Book a Class
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => document.querySelector('[data-testid="dashboard-secondary-strip"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="border-2 border-dashed border-violet-300 text-violet-700 hover:bg-violet-50 font-bold h-9 px-4" data-testid="empty-request-slot-btn">
                            <Sparkles className="w-4 h-4 mr-1.5" /> Request a Slot
                          </Button>
                        </div>
                      </div>
                    ) : (
                      allUpcoming.map((cls, idx) => {
                        const isEntry = cls._source === 'entry';
                        const topicName = isEntry ? cls.topic_name : (cls.topic_name || cls.course_name || cls.subject || 'Class Session');
                        const teacherName = isEntry ? cls.teacher_name : cls.teacher_name;
                        const date = isEntry ? cls.date : cls.date;
                        const time = isEntry ? cls.time : (cls.time || cls.start_time);
                        const status = isEntry ? cls.status : cls.status;
                        const isCarried = status === 'carried_forward';
                        const isScheduled = !isEntry && (cls.status === 'scheduled' || cls.status === 'rescheduled');
                        const canJoin = isScheduled && isJoinEnabled(cls.date, cls.time || cls.start_time);
                        // Check cancel/reschedule time rules
                        const classDateTime = date && time ? new Date(`${date}T${time}:00`) : null;
                        const nowMs = Date.now();
                        const diffMin = classDateTime ? (classDateTime.getTime() - nowMs) / 60000 : Infinity;
                        const canCancel = isScheduled && diffMin >= 60;
                        // PLATFORM RULE: students may reschedule up to 6 hours before class
                        const canReschedule = isScheduled && diffMin >= 360;
                        const color = getTopicColor(topicName, cls.course_name || cls.subject || '', status === 'demo');

                        return (
                          <div key={cls.id || idx}
                            className={`nb-card nb-card-hover p-4 sm:p-5 border-l-[6px] ${color.accent} bg-gradient-to-r ${color.bg} animate-fade-in-up cursor-default`}
                            style={{ animationDelay: `${0.1 + idx * 0.06}s` }}
                            data-testid={`upcoming-class-${cls.id || idx}`}>
                            <div className="flex gap-4">
                              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex-shrink-0 relative overflow-hidden group">
                                <img src={getTopicImage(topicName, '', false)} alt={topicName} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                                <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-white/90 rounded text-[9px] text-slate-800 font-bold">{getTopicCategory(topicName)}</div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    {isCarried ? 'Carried Forward' : isEntry ? (cls.source === 'curriculum' ? 'Curriculum Class' : 'Custom Class') : 'Scheduled Class'}
                                  </p>
                                  {isCarried && <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold">Carried</span>}
                                </div>
                                <h2 className="text-base sm:text-lg font-bold text-slate-900 truncate">{topicName}</h2>
                                {(cls.vertical || cls.module || cls.course_name) && (
                                  <p className="text-[11px] font-bold text-indigo-600 truncate mt-0.5">
                                    {[cls.vertical, cls.module].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ')}
                                  </p>
                                )}
                                {cls.topic_status === 'pending' && (
                                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-extrabold uppercase tracking-wider">
                                    Topic Pending
                                  </span>
                                )}
                                <p className="text-sm text-slate-500">with <span className="font-semibold text-slate-700">{teacherName || 'Teacher'}</span></p>
                                <div className="flex items-center gap-4 mt-2 text-sm">
                                  <span className="flex items-center gap-1.5 text-slate-700"><CalendarDays className="w-4 h-4" />{date || 'TBD'}</span>
                                  <span className="flex items-center gap-1.5 text-slate-700"><Clock className="w-4 h-4" />{time ? convertTime(time, cls.timezone || cls.student_timezone || 'IST', studentTimezone) : 'TBD'}</span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${isCarried ? 'bg-orange-100 text-orange-700' : status === 'incomplete' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {isCarried ? 'Carried Forward' : status === 'incomplete' ? 'Incomplete' : status?.charAt(0).toUpperCase() + status?.slice(1)}
                                  </span>
                                </div>
                                {/* Action Buttons */}
                                {isScheduled && (
                                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                                    {canJoin ? (
                                      <Button size="sm" onClick={() => handleJoinClass(cls)} className="bg-gradient-to-b from-blue-500 to-blue-700 hover:from-blue-400 hover:to-blue-600 text-white font-bold text-xs h-9 w-9 rounded-full p-0 shadow-[0_4px_6px_rgba(37,99,235,0.4),inset_0_1px_0_rgba(255,255,255,0.2)] hover:shadow-[0_6px_12px_rgba(37,99,235,0.5),inset_0_1px_0_rgba(255,255,255,0.3)] active:shadow-[0_1px_2px_rgba(37,99,235,0.4),inset_0_1px_3px_rgba(0,0,0,0.2)] active:translate-y-[1px] transition-all" data-testid={`join-btn-${cls.id}`}><Video className="w-4 h-4" /></Button>
                                    ) : (
                                      <Button size="sm" disabled className="bg-gradient-to-b from-slate-200 to-slate-300 text-slate-400 cursor-not-allowed font-bold text-xs h-9 w-9 rounded-full p-0 shadow-[0_2px_4px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.5)]"><Video className="w-4 h-4" /></Button>
                                    )}
                                    <Button size="sm" variant="outline"
                                      onClick={() => canReschedule ? openRescheduleDialog(cls) : toast.error('Reschedule allowed only up to 6 hours before class')}
                                      className={`text-xs h-8 px-3 font-bold ${canReschedule ? 'border-slate-300 hover:bg-slate-50' : 'opacity-50 cursor-not-allowed'}`}
                                      data-testid={`reschedule-btn-${cls.id}`}>
                                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reschedule
                                    </Button>
                                    <Button size="sm" variant="outline"
                                      onClick={() => canCancel ? (setCancelTarget(cls), setCancelDialogOpen(true)) : toast.error('Cannot cancel within 1 hour of start')}
                                      className={`text-xs h-8 px-3 font-bold border-red-200 text-red-500 hover:bg-red-50 ${canCancel ? '' : 'opacity-50 cursor-not-allowed'}`}
                                      data-testid={`cancel-btn-${cls.id}`}>
                                      <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    {allUpcoming.length > 0 && (
                      <button onClick={() => setActiveSection('schedule')} className="w-full text-center py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-slate-700 text-sm font-bold transition-all border border-slate-200" data-testid="view-all-upcoming">
                        View All Classes <ChevronRight className="w-4 h-4 inline ml-1" />
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* === COMPLETED CLASSES === */}
              {classViewTab === 'completed' && (() => {
                const completedEntries = classEntries.filter(e => e.status === 'completed');
                const completedScheduled = studentSchedule.filter(c => c.status === 'completed');
                // Also include completedClasses from /api/student/completed-classes endpoint
                const completedFromApi = completedClasses.map(c => ({ ...c, _source: 'api', _date: c.date || '', topic_name: c.topic_name || c.course_name || c.subject || 'Class Session' }));
                const allCompleted = [
                  ...completedEntries.map(e => ({ ...e, _source: 'entry', _date: e.date || '' })),
                  ...completedScheduled.map(c => ({ ...c, _source: 'schedule', _date: c.date || '', topic_name: c.topic_name || c.course_name || c.subject || 'Class Session' })),
                  ...completedFromApi
                ].sort((a, b) => b._date.localeCompare(a._date));

                return (
                  <div className="space-y-3" data-testid="completed-classes-section">
                    {allCompleted.length === 0 ? (
                      <div className="nb-card p-8 text-center">
                        <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" strokeWidth={2.5} />
                        <p className="text-slate-900 font-bold text-lg">Your trophy case is empty — for now.</p>
                        <p className="text-slate-500 text-sm mt-1">Completed classes will appear here once your teacher marks them done.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {allCompleted.map((cls, idx) => (
                          <div key={cls.id || idx}
                            className="glass-card rounded-2xl p-4 border-l-4 border-l-emerald-500 hover:shadow-lg hover:scale-[1.01] transition-all"
                            data-testid={`completed-class-${cls.id || idx}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="w-5 h-5 text-emerald-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-slate-900 text-sm truncate">{cls.topic_name}</p>
                                <p className="text-xs text-slate-400 mt-0.5">{cls.teacher_name || 'Teacher'}</p>
                                <div className="flex items-center flex-wrap gap-2 mt-1.5 text-xs text-slate-500">
                                  {cls._date && <span>{new Date(cls._date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Completed</span>
                                  {cls._source === 'entry' && cls.source === 'curriculum' && <span className="text-blue-600">[Curriculum]</span>}
                                  {cls._source === 'entry' && cls.source === 'custom' && <span className="text-amber-600">[Custom]</span>}
                                  {(cls.student_duration_minutes || cls.teacher_duration_minutes) && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold"><Clock className="w-3 h-3 inline mr-0.5" />{cls.student_duration_minutes || cls.teacher_duration_minutes} min</span>}
                                  {cls.previous_curriculum && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">From Previous Curriculum</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {[
                  { label: 'Classes Left', value: classCredits.total_purchased > 0 ? classCredits.classes_remaining : classAllocation.remaining, icon: BookOpen, color: 'bg-emerald-500/20 text-emerald-400' },
                  { label: 'Completed', value: classCredits.total_purchased > 0 ? classCredits.classes_completed : classAllocation.completed, icon: CheckCircle, color: 'bg-blue-500/20 text-blue-400' },
                  { label: 'Purchased', value: classCredits.total_purchased || classAllocation.total_assigned, icon: CreditCard, color: 'bg-purple-500/20 text-purple-400' },
                  { label: 'Assignments', value: assignments.length, icon: ClipboardList, color: 'bg-amber-500/20 text-amber-400' },
                ].map((s,i) => (
                  <div key={i} className="glass-card rounded-2xl p-5 shadow-sm hover:shadow-lg hover:scale-[1.02] transition-all" data-testid={`stat-${s.label.toLowerCase().replace(/ /g,'-')}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl ${s.color} flex items-center justify-center`}><s.icon className="w-6 h-6" /></div>
                      <div><p className="text-3xl font-bold text-slate-900">{s.value}</p><p className="text-sm text-slate-400 font-semibold">{s.label}</p></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Mapped Curriculum */}
              {curricula.length > 0 && (
                <div className="glass-card rounded-2xl p-4 sm:p-6 shadow-sm">
                  <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2 text-lg"><Target className="w-5 h-5 text-blue-400" /> Mapped Curriculum</h3>
                  <div className="space-y-4">
                    {curricula.map((c, i) => {
                      const allTopics = c.topics || c.classes || [];
                      const totalTopics = allTopics.length;
                      const completedTopics = allTopics.filter(t => t.status === 'completed').length;
                      const carriedTopics = allTopics.filter(t => t.status === 'carried_forward').length;
                      const pendingTopics = totalTopics - completedTopics - carriedTopics;
                      const pct = totalTopics ? Math.round((completedTopics / totalTopics) * 100) : 0;
                      // Cross-reference with class entries for this curriculum
                      const currEntries = classEntries.filter(e => e.curriculum_id === c.id);
                      const entryCompleted = currEntries.filter(e => e.status === 'completed').length;

                      return (
                        <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-slate-200 transition-all" data-testid={`curriculum-${c.id || i}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-base font-bold text-slate-900">{c.course_name || c.module_name || c.subject}</p>
                              <p className="text-sm text-slate-400">{c.vertical}{c.category ? ` / ${c.category}` : ''} &middot; by {c.teacher_name}</p>
                            </div>
                            <div className="text-right">
                              <span className="text-2xl font-extrabold text-blue-400">{pct}%</span>
                              <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                <span className="text-emerald-400">{completedTopics} done</span>
                                {carriedTopics > 0 && <span className="text-orange-400">{carriedTopics} carried</span>}
                                <span className="text-slate-500">{pendingTopics} pending</span>
                              </div>
                            </div>
                          </div>
                          <Progress value={pct} className="h-2.5 mb-3" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {allTopics.map((t, ti) => {
                              const isComplete = t.status === 'completed';
                              const isProgress = t.status === 'in_progress';
                              const isCarried = t.status === 'carried_forward';
                              // Check if there's a matching class entry
                              const matchEntry = classEntries.find(e => e.topic_name === (t.title || t.name) && e.curriculum_id === c.id);
                              return (
                                <div key={ti}
                                  className={`relative overflow-hidden rounded-xl border-2 transition-all hover:shadow-md hover:scale-[1.01] ${isComplete ? 'border-emerald-400/50 bg-emerald-900/20' : isCarried ? 'border-orange-400/50 bg-orange-900/20' : isProgress ? 'border-blue-400/50 bg-blue-900/20' : 'border-slate-200 bg-slate-50'}`}
                                  data-testid={`topic-${c.id || i}-${ti}`}>
                                  <div className="relative p-3 flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-emerald-500' : isCarried ? 'bg-orange-500' : isProgress ? 'bg-blue-500' : 'bg-slate-600'}`}>
                                      {isComplete ? <CheckCircle className="w-5 h-5 text-slate-900" /> :
                                       isCarried ? <RefreshCw className="w-5 h-5 text-slate-900" /> :
                                       isProgress ? <Clock className="w-5 h-5 text-slate-900" /> :
                                       <BookOpen className="w-5 h-5 text-slate-900" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className={`text-sm font-bold truncate ${isComplete ? 'text-emerald-300' : isCarried ? 'text-orange-300' : isProgress ? 'text-blue-300' : 'text-slate-300'}`}>{t.title || t.name || `Topic ${ti + 1}`}</p>
                                      <p className={`text-xs font-semibold capitalize ${isComplete ? 'text-emerald-500' : isCarried ? 'text-orange-500' : isProgress ? 'text-blue-500' : 'text-slate-500'}`}>
                                        {isComplete ? 'Completed' : isCarried ? 'Carried Forward' : isProgress ? 'In Progress' : 'Pending'}
                                        {matchEntry && matchEntry.date && <span className="ml-2 text-slate-500">&middot; {new Date(matchEntry.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </div> {/* /dashboard-right-panel wrapper */}

              {/* Below-the-fold: Monthly Report Card + Quick Links (Scheduler/Leaderboard live in the right rail) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2" data-testid="dashboard-secondary-strip">
                <StudentReportCardArchive studentId={user?.id} />
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 card-hover">
                  <h3 className="text-xs font-bold text-slate-800 mb-3">Quick Links</h3>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Learning Path', section: 'learning-path', icon: Target, color: 'text-indigo-500' },
                      { label: 'Report Card', section: 'report-card', icon: FileText, color: 'text-blue-500' },
                      { label: 'Rate a Class', section: 'rate-class', icon: Star, color: 'text-amber-500' },
                    ].map((link) => (
                      <button key={link.section} onClick={() => setActiveSection(link.section)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:translate-x-1 transition-all duration-200" data-testid={`quick-${link.section}`}>
                        <link.icon className={`w-3.5 h-3.5 ${link.color}`} /> {link.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              </div> {/* /dashboard-main-column */}
            </div>
          )}

          {/* Schedule */}
          {activeSection === 'schedule' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-black">My Schedule</h1>
                <Select value={scheduleMonth} onValueChange={setScheduleMonth}>
                  <SelectTrigger className="w-44 h-10 rounded-xl" data-testid="schedule-month-select">
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {scheduleMonths.map(m => (
                      <SelectItem key={m} value={m}>{new Date(m + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                {filteredSchedule.length === 0 && <p className="text-slate-500 font-semibold text-base text-center py-10">No classes scheduled</p>}
                {filteredSchedule.map((cls) => (
                  <div key={cls.id} className={`glass-card rounded-2xl border p-5 ${cls.status === 'completed' ? 'border-emerald-400/30 bg-emerald-500/10/30' : 'border-black/15'}`} data-testid={`schedule-${cls.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cls.status === 'completed' ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                          {cls.status === 'completed' ? <CheckCircle className="w-6 h-6 text-emerald-700 font-bold" /> : <Calendar className="w-6 h-6 text-blue-600" />}
                        </div>
                        <div>
                          <p className="font-bold text-black text-base">{cls.course_name || cls.subject || 'Class'}</p>
                          {cls.topic_name && <p className="text-xs font-bold text-indigo-600">Topic: {cls.topic_name}</p>}
                          <p className="text-sm text-slate-500 font-semibold">{cls.teacher_name}</p>
                          <div className="flex items-center gap-3 text-sm text-slate-400 font-semibold mt-1">
                            <span>{cls.date}</span><span>{convertTime(cls.time || cls.start_time, cls.timezone || cls.student_timezone || 'IST', studentTimezone)}</span><span className="text-blue-500 font-medium">({studentTimezone})</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {cls.status === 'completed' ? (
                          <>
                            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 font-bold text-sm rounded-full font-semibold">Done</span>
                            {cls.recording_url && (
                              <a
                                href={cls.recording_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs font-semibold"
                                data-testid={`watch-recording-${cls.id}`}
                              >
                                <Video className="w-3.5 h-3.5" /> Watch Recording
                              </a>
                            )}
                          </>
                        ) : (cls.status === 'scheduled' || cls.status === 'rescheduled') ? (
                          <>
                            {isJoinEnabled(cls.date, cls.time || cls.start_time) ? (
                              <Button size="sm" onClick={() => handleJoinClass(cls)} className="bg-gradient-to-b from-blue-500 to-blue-700 hover:from-blue-400 hover:to-blue-600 text-white h-9 w-9 rounded-full p-0 shadow-[0_4px_6px_rgba(37,99,235,0.4),inset_0_1px_0_rgba(255,255,255,0.2)] hover:shadow-[0_6px_12px_rgba(37,99,235,0.5),inset_0_1px_0_rgba(255,255,255,0.3)] active:shadow-[0_1px_2px_rgba(37,99,235,0.4),inset_0_1px_3px_rgba(0,0,0,0.2)] active:translate-y-[1px] transition-all" data-testid={`join-sched-${cls.id}`}>
                                <Video className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button size="sm" disabled className="bg-gradient-to-b from-slate-200 to-slate-300 text-slate-400 h-9 w-9 rounded-full p-0 shadow-[0_2px_4px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.5)] cursor-not-allowed"><Video className="w-4 h-4" /></Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => openRescheduleDialog(cls)} className="text-sm border-slate-300 h-9 px-4" data-testid={`reschedule-${cls.id}`}>
                              <RefreshCw className="w-4 h-4 mr-1" /> Reschedule
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setCancelTarget(cls); setCancelDialogOpen(true); }} className="text-sm border-red-400/30 text-red-500 hover:bg-red-500/10 h-9 px-4" data-testid={`cancel-${cls.id}`}>
                              <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                          </>
                        ) : (
                          <span className="px-3 py-1 bg-slate-100 text-slate-600 font-semibold text-sm rounded-full">{cls.status}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Report Card */}
          {activeSection === 'report-card' && (
            <div className="space-y-5">
              <h1 className="text-2xl font-bold text-black">Monthly Report Cards</h1>
              {viewingReportCard ? (
                <div className="glass-card rounded-xl overflow-hidden shadow-sm" data-testid="student-rc-view">
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-5">
                    <div className="flex items-center justify-between">
                      <div><h2 className="text-lg font-bold text-slate-900">Monthly Report for {viewingReportCard.month_label}</h2></div>
                      <div className="text-right"><p className="text-slate-900 font-bold text-sm">{viewingReportCard.student_name}</p><p className="text-indigo-200 text-xs">by {viewingReportCard.teacher_name}</p></div>
                    </div>
                  </div>
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-blue-500/10 rounded-xl border border-blue-400/30"><div className="text-2xl font-bold text-blue-600">{viewingReportCard.classes_this_month}</div><div className="text-xs text-slate-500 font-semibold">Classes This Month</div></div>
                      <div className="text-center p-3 bg-emerald-500/10 rounded-xl border border-emerald-400/30"><div className="text-2xl font-bold text-emerald-700">{viewingReportCard.total_classes_completed}</div><div className="text-xs text-slate-500 font-semibold">Total All Time</div></div>
                      <div className="text-center p-3 bg-purple-500/10 rounded-xl border border-purple-400/30"><div className="text-2xl font-bold text-purple-600">{viewingReportCard.projects_completed?.length || 0}</div><div className="text-xs text-slate-500 font-semibold">Projects Done</div></div>
                    </div>
                    {/* Classes completed this month - detailed list */}
                    {(viewingReportCard.classes_this_month_detail?.length > 0 || viewingReportCard.classes_this_month > 0) && (
                      <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-200/50">
                        <p className="text-sm font-bold text-blue-900 mb-3">Classes Completed in {viewingReportCard.month_label}</p>
                        {viewingReportCard.classes_this_month_detail?.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-blue-200/50">
                                  <th className="text-left py-2 px-3 font-bold text-blue-700">#</th>
                                  <th className="text-left py-2 px-3 font-bold text-blue-700">Date</th>
                                  <th className="text-left py-2 px-3 font-bold text-blue-700">Time</th>
                                  <th className="text-left py-2 px-3 font-bold text-blue-700">Course</th>
                                  <th className="text-left py-2 px-3 font-bold text-blue-700">Topic</th>
                                </tr>
                              </thead>
                              <tbody>
                                {viewingReportCard.classes_this_month_detail.map((cls, idx) => (
                                  <tr key={idx} className="border-b border-blue-100/50 last:border-0">
                                    <td className="py-2 px-3 font-bold text-black">{idx + 1}</td>
                                    <td className="py-2 px-3 font-bold text-black">{cls.date ? new Date(cls.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}</td>
                                    <td className="py-2 px-3 font-bold text-black">{cls.time || '-'}</td>
                                    <td className="py-2 px-3 font-bold text-black">{cls.course_name || '-'}</td>
                                    <td className="py-2 px-3 font-bold text-black">{cls.topic_name || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-600 font-semibold">{viewingReportCard.classes_this_month} class(es) completed this month</p>
                        )}
                      </div>
                    )}
                    {viewingReportCard.projects_completed?.length > 0 && (
                      <div className="p-3 bg-slate-500/10 rounded-xl border border-black/15">
                        <p className="text-xs font-bold text-slate-700 mb-2">Projects Completed</p>
                        <div className="flex flex-wrap gap-2">
                          {viewingReportCard.projects_completed.map((p, i) => (
                            <span key={i} className="px-2 py-1 bg-violet-100 text-violet-700 rounded-lg text-xs font-bold">{p.title}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {viewingReportCard.teacher_feedback && <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-200"><p className="text-xs font-bold text-teal-900 mb-1">Teacher Feedback</p><p className="text-slate-700 font-bold text-sm">{viewingReportCard.teacher_feedback}</p></div>}
                    {viewingReportCard.areas_of_improvement && <div className="p-3 bg-amber-500/10 rounded-xl border border-amber-400/30"><p className="text-xs font-bold text-amber-900 mb-1">Areas of Improvement</p><p className="text-slate-700 font-bold text-sm">{viewingReportCard.areas_of_improvement}</p></div>}
                    {viewingReportCard.overall_progress && <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-200"><p className="text-xs font-bold text-indigo-900 mb-1">Overall Progress</p><p className="text-slate-700 text-sm">{viewingReportCard.overall_progress}</p></div>}
                    {viewingReportCard.final_grade && <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200"><span className="text-sm font-bold text-slate-700">Final Grade:</span><span className="text-3xl font-bold text-emerald-600 px-3 py-1 border-2 border-emerald-400 rounded-lg">{viewingReportCard.final_grade}</span></div>}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setViewingReportCard(null)} className="flex-1">Back to Reports</Button>
                      {viewingReportCard.emailed ? (
                        <Button size="sm" disabled className="bg-slate-100 text-slate-400"><Mail className="w-4 h-4 mr-1" /> Emailed</Button>
                      ) : (
                        <Button size="sm" onClick={async () => {
                          try {
                            const res = await api.post(`/teacher/report-card/${viewingReportCard.id}/email`);
                            toast.success(res.data.message);
                            setViewingReportCard({ ...viewingReportCard, emailed: true });
                            fetchReportCards();
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
                        }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="email-report-card"><Mail className="w-4 h-4 mr-1" /> Email to Parent</Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : reportCardsData.length === 0 ? (
                <div className="glass-card rounded-xl p-10 text-center"><FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500 font-semibold text-sm">No monthly reports yet</p></div>
              ) : (
                <div className="space-y-2">{reportCardsData.map(rc => (
                  <button key={rc.id} onClick={() => setViewingReportCard(rc)} className="w-full glass-card rounded-xl p-4 text-left hover:border-blue-300 transition-all" data-testid={`student-rc-${rc.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><FileText className="w-5 h-5 text-indigo-600" /></div><div><p className="font-bold text-black">Monthly Report for {rc.month_label}</p><p className="text-xs text-slate-500 font-bold">by {rc.teacher_name} | {rc.classes_this_month} classes</p></div></div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </button>
                ))}</div>
              )}
            </div>
          )}

          {/* Rate Class */}
          {activeSection === 'rate-class' && (
            <div className="space-y-5">
              <h1 className="text-2xl font-bold text-black">Rate Completed Classes</h1>
              {completedClasses.length === 0 ? (
                <div className="glass-card rounded-xl p-10 text-center"><Star className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500 font-semibold text-sm">No completed classes to rate</p></div>
              ) : (
                <div className="space-y-2">{completedClasses.map(cls => (
                  <div key={cls.id} className="glass-card rounded-xl p-3 flex items-center justify-between" data-testid={`rate-class-${cls.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center"><Star className="w-4 h-4 text-amber-600" /></div>
                      <div><p className="font-bold text-black text-sm">{cls.course_name || cls.subject || 'Class'}</p><p className="text-xs text-slate-500 font-semibold">{cls.teacher_name} &middot; {cls.date}</p></div>
                    </div>
                    {cls.already_rated ? (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 font-bold text-xs rounded-full font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Rated</span>
                    ) : (
                      <Button size="sm" onClick={() => { setRatingTarget(cls); setRatingDialogOpen(true); }} className="bg-amber-500/100 hover:bg-amber-600 text-white text-xs h-7" data-testid={`rate-btn-${cls.id}`}><Star className="w-3 h-3 mr-1" /> Rate</Button>
                    )}
                  </div>
                ))}</div>
              )}
            </div>
          )}

          {/* Discussions */}
          {activeSection === 'discussions' && (
            <div className="space-y-5">
              <h1 className="text-2xl font-bold text-black">Discussions</h1>
              {selectedThread ? (
                <div className="glass-card rounded-xl shadow-sm">
                  <div className="p-3 border-b border-black/10">
                    <button onClick={() => setSelectedThread(null)} className="text-xs text-blue-600 hover:underline mb-1 block">&larr; Back</button>
                    <h3 className="font-bold text-black text-sm">{selectedThread.subject}</h3>
                  </div>
                  <div className="p-3 space-y-2 max-h-80 overflow-y-auto" data-testid="thread-messages">
                    {(selectedThread.messages || []).map(msg => (
                      <div key={msg.id} className={`flex ${msg.sender_role === 'student' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm ${msg.sender_role === 'student' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800 font-semibold'}`}>
                          <p>{msg.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 border-t border-black/10 flex gap-2">
                    <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type a message..." className="flex-1" data-testid="thread-message-input" onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()} />
                    <Button onClick={handleSendMessage} className="bg-blue-600 hover:bg-blue-700" data-testid="send-message-btn"><Send className="w-4 h-4" /></Button>
                  </div>
                </div>
              ) : threads.length === 0 ? (
                <div className="glass-card rounded-xl p-10 text-center"><MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500 font-semibold text-sm">No discussions yet</p></div>
              ) : (
                <div className="space-y-2">{threads.map(t => (
                  <button key={t.id} onClick={() => api.get(`/discussion-threads/${t.id}`).then(r => setSelectedThread(r.data)).catch(() => setSelectedThread(t))} className="w-full glass-card rounded-xl p-3 text-left hover:border-blue-300" data-testid={`thread-${t.id}`}>
                    <div className="flex items-center justify-between"><div><p className="font-bold text-black text-sm">{t.subject}</p><p className="text-xs text-slate-500 font-semibold">with {t.teacher_name}</p></div><ChevronRight className="w-4 h-4 text-slate-400 font-semibold" /></div>
                  </button>
                ))}</div>
              )}
            </div>
          )}

          {/* Homework (new LMS feature) */}
          {activeSection === 'homework' && (
            <StudentHomeworkSection />
          )}

          {/* Coding Projects (Phase 3) */}
          {activeSection === 'coding-projects' && (
            <StudentCodingProjects />
          )}

          {/* Assignments */}
          {activeSection === 'assignments' && (
            <div className="space-y-5">
              <h1 className="text-2xl font-bold text-black">Assignments</h1>
              {assignments.length === 0 ? (
                <div className="glass-card rounded-xl p-10 text-center"><ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500 font-semibold text-sm">No assignments yet</p></div>
              ) : (
                <div className="space-y-2">{assignments.map(a => (
                  <div key={a.id} className="glass-card rounded-xl p-3" data-testid={`assignment-${a.id}`}>
                    <div className="flex items-center justify-between">
                      <div><p className="font-bold text-black text-sm">{a.title}</p><p className="text-xs text-slate-500 font-semibold">{a.description}</p><p className="text-[11px] text-slate-400 font-semibold mt-0.5">Due: {a.due_date || 'Not set'}</p></div>
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${a.status === 'submitted' ? 'bg-emerald-100 text-emerald-700 font-bold' : a.status === 'graded' ? 'bg-blue-100 text-blue-600 font-bold' : 'bg-amber-100 text-amber-700'}`}>{a.status || 'Pending'}</span>
                    </div>
                  </div>
                ))}</div>
              )}
            </div>
          )}

          {/* Learning Zone */}
          {activeSection === 'learning-zone' && (
            <div className="space-y-5" data-testid="learning-zone-section">
              <h1 className="text-2xl font-bold text-black">Learning Zone</h1>
              {learningLinks.length === 0 ? (
                <div className="glass-card rounded-xl p-10 text-center">
                  <Link2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 font-semibold text-base">No learning links yet</p>
                  <p className="text-sm text-slate-400 font-semibold mt-1">Your teacher will assign helpful links and resources here</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {learningLinks.map(link => (
                    <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
                      className="glass-card rounded-2xl p-5 hover:shadow-lg hover:border-teal-300 transition-all group cursor-pointer" data-testid={`learning-link-${link.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <Link2 className="w-5 h-5 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-black text-base group-hover:text-teal-700 truncate">{link.title}</h3>
                          {link.description && <p className="text-sm text-slate-500 font-semibold mt-1 line-clamp-2">{link.description}</p>}
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-teal-600 font-medium truncate">{link.url}</span>
                            <ExternalLink className="w-3 h-3 text-teal-400 flex-shrink-0" />
                          </div>
                          <p className="text-xs text-slate-400 font-semibold mt-1">Shared by {link.teacher_name}</p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}



          {/* Learning Path */}
          {activeSection === 'learning-path' && <LearningPath />}



          {/* Badges & Achievements */}
          {activeSection === 'badges' && (
            <div className="space-y-6" data-testid="badges-section">
              <div>
                <h1 className="text-2xl font-bold text-[#0F172A]">Badges & Achievements</h1>
                <p className="text-sm text-[#334155] font-semibold">Track your progress and earn rewards</p>
              </div>

              {/* XP progress bar */}
              <StudentXPBar vertical={activeVertical} />

              {/* Certificates strip */}
              <div>
                <h2 className="text-lg font-extrabold text-[#0F172A] mb-2">Certificates</h2>
                <StudentCertificates vertical={activeVertical} />
              </div>

              {/* Feedback-driven Progress Badges */}
              <div>
                <h2 className="text-lg font-extrabold text-[#0F172A] mb-2">Feedback Badges</h2>
                <ProgressBadges studentId={user?.id} dataTestidPrefix="student-badges" />
              </div>

              {/* Weekly Math Olympiad teaser */}
              {olympiadActive?.challenge && (
                <div
                  className="rounded-2xl p-5 text-white shadow-sm cursor-pointer hover:scale-[1.005] transition-transform"
                  style={{ background: 'linear-gradient(135deg, #F59E0B, #DC2626)' }}
                  onClick={() => setOlympiadOpen(true)}
                  data-testid="olympiad-teaser-card"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] font-extrabold uppercase tracking-wider bg-white/25 px-2 py-0.5 rounded-full">{olympiadActive.challenge.week_iso}</span>
                        <h3 className="text-base font-extrabold">{olympiadActive.challenge.title}</h3>
                      </div>
                      <p className="text-xs font-bold opacity-90">
                        Theme: {olympiadActive.challenge.theme} · {olympiadActive.challenge.questions?.length || 0} questions
                      </p>
                    </div>
                    <Button
                      className="rounded-full bg-white text-amber-600 hover:bg-white/90 font-extrabold text-xs px-4"
                      data-testid="olympiad-card-btn"
                    >
                      {olympiadActive.user_attempt ? 'View Result' : 'Attempt Now'}
                    </Button>
                  </div>
                </div>
              )}

              {badgeData ? (
                <>
                  {/* Level Card */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="student-level">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-extrabold text-lg ${
                        badgeData.level === 'Advanced' ? 'bg-gradient-to-br from-purple-500 to-indigo-600' :
                        badgeData.level === 'Intermediate' ? 'bg-gradient-to-br from-blue-500 to-cyan-500' :
                        'bg-gradient-to-br from-amber-400 to-orange-500'
                      }`}>
                        {badgeData.level === 'Advanced' ? <Trophy className="w-8 h-8" /> : badgeData.level === 'Intermediate' ? <TrendingUp className="w-8 h-8" /> : <Star className="w-8 h-8" />}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#334155] uppercase tracking-wider">Current Level</p>
                        <p className="text-2xl font-extrabold text-[#0F172A]">{badgeData.level}</p>
                        <p className="text-xs text-[#334155]">{badgeData.topics_completed} topics completed, {badgeData.classes_completed} classes done</p>
                      </div>
                    </div>
                    {/* Level Progress */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs font-bold text-[#334155] mb-1">
                        <span>Beginner</span><span>Intermediate (10)</span><span>Advanced (20)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-3 relative">
                        <div className="bg-gradient-to-r from-amber-400 via-blue-500 to-purple-600 h-3 rounded-full transition-all" style={{ width: `${Math.min(100, badgeData.topics_completed / 20 * 100)}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Badge Grid */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <h3 className="font-bold text-[#0F172A] mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" /> Your Badges</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {badgeData.badges.map((badge, i) => (
                        <div key={i} className={`text-center p-4 rounded-xl border-2 transition-all ${
                          badge.earned ? 'bg-amber-50 border-amber-300 shadow-md hover:shadow-lg hover:-translate-y-0.5' : 'bg-slate-50 border-slate-200 opacity-50'
                        }`} data-testid={`student-badge-${badge.id}`}>
                          <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-2 ${
                            badge.earned ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : 'bg-slate-200 text-slate-400'
                          }`}>
                            {badge.icon === 'award' && <Award className="w-7 h-7" />}
                            {badge.icon === 'star' && <Star className="w-7 h-7" />}
                            {badge.icon === 'trophy' && <Trophy className="w-7 h-7" />}
                            {badge.icon === 'zap' && <TrendingUp className="w-7 h-7" />}
                            {badge.icon === 'code' && <BookOpen className="w-7 h-7" />}
                          </div>
                          <p className="text-sm font-bold text-[#0F172A]">{badge.name}</p>
                          <p className={`text-xs font-bold mt-0.5 ${badge.earned ? 'text-amber-600' : 'text-slate-400'}`}>
                            {badge.earned ? 'Earned' : 'Locked'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Continue Learning CTA */}
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-6 text-white text-center">
                    <h3 className="text-xl font-extrabold mb-1">Keep going!</h3>
                    <p className="text-blue-100 text-sm font-medium mb-3">Complete more topics to level up and earn new badges</p>
                    <button onClick={() => setActiveSection('learning-path')} className="bg-white text-blue-600 font-extrabold px-6 py-2.5 rounded-full text-sm hover:shadow-lg transition-shadow" data-testid="continue-learning-btn">
                      Continue Learning
                    </button>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
                  <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-[#334155] font-bold">Loading badges...</p>
                </div>
              )}
            </div>
          )}


          {/* My Files */}
          {activeSection === 'my-files' && <MyFilesPage />}

          {/* CLASS FEEDBACK TIMELINE */}
          {activeSection === 'feedback' && (
            <div className="p-3 sm:p-6 max-w-4xl">
              <h1 className="text-xl sm:text-2xl font-bold text-black mb-4" data-testid="student-feedback-title">Class Feedback</h1>
              <p className="text-sm text-slate-500 mb-4">Feedback from your teachers after every class.</p>
              <FeedbackTimeline studentId={user?.id} dataTestidPrefix="student-timeline" />
            </div>
          )}


          {/* Settings */}
          {activeSection === 'settings' && (
            <div className="space-y-5">
              <h1 className="text-2xl font-bold text-black">Settings</h1>
              <div className="glass-card rounded-xl p-5 shadow-sm space-y-5">
                <div>
                  <h3 className="font-bold text-black mb-2 text-sm">Profile Picture</h3>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {profilePicture ? (<img src={`${API_BASE}${profilePicture}`} alt="Profile" className="w-16 h-16 rounded-full object-cover border-3 border-blue-400/30" />) : (<div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center border-3 border-blue-400/30"><User className="w-7 h-7 text-blue-600 font-bold" /></div>)}
                      <button onClick={() => fileInputRef.current?.click()} className="absolute bottom-0 right-0 bg-blue-600 text-white p-1 rounded-full shadow-md hover:bg-blue-700" data-testid="upload-pic-btn"><Camera className="w-3 h-3" /></button>
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfilePicUpload} className="hidden" />
                    </div>
                  </div>
                </div>
                <div><h3 className="font-bold text-black mb-2 text-sm">Grade</h3><Input value={settingsGrade} onChange={(e) => setSettingsGrade(e.target.value)} placeholder="e.g. 5th, 8th" className="max-w-xs" data-testid="settings-grade" /></div>
                <div>
                  <h3 className="font-bold text-black mb-2 text-sm">Timezone</h3>
                  <Select value={settingsTimezone} onValueChange={setSettingsTimezone}>
                    <SelectTrigger className="max-w-xs" data-testid="settings-timezone"><SelectValue /></SelectTrigger>
                    <SelectContent>{['IST', 'EST', 'PST', 'GMT', 'SGT', 'CST', 'CET', 'AEST'].map(tz => (<SelectItem key={tz} value={tz}>{tz} ({getCurrentTimeInTZ(tz)})</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-settings-btn">{savingSettings ? 'Saving...' : 'Save Settings'}</Button>
              </div>

              {/* Privacy — Class Recordings */}
              <div className="glass-card rounded-xl p-5 shadow-sm space-y-3" data-testid="recording-privacy-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-slate-900 flex items-center gap-2"><Video className="w-4 h-4 text-blue-600" /> Class Recordings</h3>
                    <p className="text-xs text-slate-500 mt-1 max-w-md">
                      Teachers may upload recordings of your live classes for you to rewatch later. Turn this off if you prefer none of your classes be recorded.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!recordingDisabled}
                    disabled={savingRecordingPref}
                    onClick={() => handleToggleRecording(!recordingDisabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${!recordingDisabled ? 'bg-emerald-500' : 'bg-slate-300'} disabled:opacity-50`}
                    data-testid="recording-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!recordingDisabled ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                </div>
                <div className={`text-xs font-semibold ${recordingDisabled ? 'text-amber-700' : 'text-emerald-700'}`}>
                  {recordingDisabled ? 'Off — your teachers cannot upload recordings of your classes.' : 'On — recordings will be available to rewatch after class.'}
                </div>
              </div>

              {/* Payment History */}
              <div className="glass-card rounded-xl p-5 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-900 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-600" /> Payment History</h3>
                {paymentHistory.length === 0 ? (
                  <p className="text-sm text-slate-400">No payment records yet</p>
                ) : (
                  <div className="space-y-2">
                    {paymentHistory.map((p, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3 border border-slate-100" data-testid={`payment-row-${i}`}>
                        <div>
                          <p className="text-sm font-bold text-slate-900">{p.currency} {p.amount?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">{p.num_classes} classes — {p.payment_method}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-600">{p.payment_date}</p>
                          <p className="text-[10px] text-slate-400 font-mono">{p.transaction_ref}</p>
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold"><CheckCircle className="w-2.5 h-2.5" /> Paid</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── PROGRESS REPORT ── */}
          {activeSection === 'progress-report' && (
            <div className="space-y-6" data-testid="progress-report-section">
              <h1 className="text-2xl font-bold text-[#0F172A]">Progress Report</h1>
              {progressLoading ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-[#3B82F6]" /></div>
              ) : progressReport ? (
                <div className="space-y-5">
                  {/* Overall Stats */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-[#0F172A] text-sm">Overall Completion</h3>
                      <span className="text-lg font-bold text-[#3B82F6]">{progressReport.overall_completion_pct}%</span>
                    </div>
                    <Progress value={progressReport.overall_completion_pct} className="h-4 mb-4" />
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center bg-[#F0F7FF] rounded-lg p-3"><p className="text-xl font-bold text-[#3B82F6]">{progressReport.total_topics}</p><p className="text-[10px] text-[#64748B] font-medium">Total Topics</p></div>
                      <div className="text-center bg-[#ECFDF5] rounded-lg p-3"><p className="text-xl font-bold text-emerald-600">{progressReport.completed_topics}</p><p className="text-[10px] text-[#64748B] font-medium">Completed</p></div>
                    </div>
                  </div>

                  {/* Module Breakdown */}
                  {(progressReport.modules || []).length > 0 && (
                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                      <h3 className="font-bold text-[#0F172A] text-sm mb-4">Module Progress</h3>
                      <div className="space-y-4">
                        {progressReport.modules.map((mod, i) => (
                          <div key={i} className="border border-slate-100 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-bold text-[#0F172A] text-sm">{mod.name}</h4>
                              <span className="text-xs font-bold text-[#3B82F6]">{mod.completion_pct}% ({mod.completed_topics}/{mod.total_topics})</span>
                            </div>
                            <Progress value={mod.completion_pct} className="h-2 mb-3" />
                            <div className="space-y-1.5">
                              {(mod.topics || []).map((t, ti) => (
                                <div key={ti} className="flex items-center gap-2">
                                  {t.status === 'completed' ? (
                                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                  ) : (
                                    <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" />
                                  )}
                                  <span className={`text-xs ${t.status === 'completed' ? 'text-[#0F172A] font-medium' : 'text-[#64748B]'}`}>{t.name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Teacher Feedback */}
                  {(progressReport.recent_feedback || []).length > 0 && (
                    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                      <h3 className="font-bold text-[#0F172A] text-sm mb-3">Teacher Feedback</h3>
                      <div className="space-y-3">
                        {progressReport.recent_feedback.map((fb, i) => (
                          <div key={i} className="bg-[#F8FAFC] rounded-lg p-4 border border-slate-100">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-bold text-[#3B82F6]">{fb.subject}</span>
                              <span className="text-[10px] text-[#64748B]">{fb.date}</span>
                            </div>
                            <p className="text-sm text-[#0F172A]">{fb.feedback}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <BarChart3 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-[#64748B] text-sm">No progress data available yet. Start completing classes!</p>
                </div>
              )}
            </div>
          )}

          {/* ── PARENT HUB ── */}
          {activeSection === 'parent-hub' && (
            <div className="space-y-6" data-testid="parent-hub-section">
              <h1 className="text-2xl font-bold text-[#0F172A]">Parent Hub</h1>
              <ParentMetricsRow metrics={parentHubData?.unified_metrics} loading={parentHubLoading} />
              {parentHubLoading ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-[#3B82F6]" /></div>
              ) : parentHubData ? (
                <>
                <div className="space-y-5">
                  {/* Student Overview */}
                  <div className="nb-card p-6" data-testid="parent-hub-overview">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-[#F0F7FF] flex items-center justify-center"><User className="w-7 h-7 text-[#3B82F6]" /></div>
                        <div>
                          <h2 className="text-lg font-bold text-[#0F172A]">{user?.name}</h2>
                          <p className="text-sm text-[#64748B]">Student ID: {user?.citt_id || user?.student_id || 'N/A'}</p>
                        </div>
                      </div>
                      <Button onClick={() => setBuyClassesOpen(true)} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold rounded-xl shadow-sm" data-testid="buy-classes-btn">
                        <CreditCard className="w-4 h-4 mr-2" /> Buy More Classes
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-[#F0F7FF] rounded-xl p-4 text-center"><p className="text-2xl font-bold text-[#3B82F6]">{parentHubData.credits?.total_purchased || 0}</p><p className="text-xs text-[#64748B] font-medium">Total Classes</p></div>
                      <div className="bg-[#ECFDF5] rounded-xl p-4 text-center"><p className="text-2xl font-bold text-emerald-600">{parentHubData.credits?.classes_completed || 0}</p><p className="text-xs text-[#64748B] font-medium">Completed</p></div>
                      <div className="bg-[#FFFBEB] rounded-xl p-4 text-center"><p className="text-2xl font-bold text-amber-600">{parentHubData.credits?.classes_remaining || 0}</p><p className="text-xs text-[#64748B] font-medium">Remaining</p></div>
                      <div className="bg-[#FEF2F2] rounded-xl p-4 text-center"><p className="text-2xl font-bold text-rose-600">{parentHubData.attendance_pct || 0}%</p><p className="text-xs text-[#64748B] font-medium">Attendance</p></div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="nb-card p-5">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-[#0F172A] text-sm">Overall Progress</h3>
                      <span className="text-sm font-bold text-[#3B82F6]">{(parentHubData.credits?.total_purchased || 0) > 0 ? Math.round(((parentHubData.credits?.classes_completed || 0) / parentHubData.credits.total_purchased) * 100) : 0}%</span>
                    </div>
                    <Progress value={(parentHubData.credits?.total_purchased || 0) > 0 ? Math.round(((parentHubData.credits?.classes_completed || 0) / parentHubData.credits.total_purchased) * 100) : 0} className="h-3" />
                  </div>

                  {/* Upcoming Classes */}
                  <div className="nb-card p-5">
                    <h3 className="font-bold text-[#0F172A] text-sm mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-[#3B82F6]" /> Upcoming Classes</h3>
                    {(parentHubData.upcoming_classes || []).length > 0 ? (
                      <div className="space-y-2">
                        {parentHubData.upcoming_classes.slice(0, 5).map((cls, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg border border-slate-100">
                            <div>
                              <p className="text-sm font-bold text-[#0F172A]">{cls.topic_name || cls.subject || 'Class'}</p>
                              <p className="text-xs text-[#64748B]">{cls.date} at {convertTime(cls.time || cls.start_time, cls.timezone || 'IST', studentTimezone)}</p>
                            </div>
                            <span className="px-2 py-0.5 bg-[#F0F7FF] text-[#3B82F6] text-[10px] font-bold rounded-full">{cls.status}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#64748B] text-center py-4">No upcoming classes scheduled</p>
                    )}
                  </div>

                  {/* Per-Vertical Breakdown */}
                  {(parentHubData.verticals_breakdown || []).length > 0 && (
                    <div className="nb-card p-5" data-testid="parent-hub-verticals">
                      <h3 className="font-bold text-[#0F172A] text-sm mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-indigo-600" /> Subjects Subscribed & Consumed</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {parentHubData.verticals_breakdown.map(v => {
                          const pct = v.classes_purchased > 0 ? Math.min(100, Math.round((v.classes_consumed / v.classes_purchased) * 100)) : 0;
                          return (
                            <div key={v.vertical} className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={`vertical-row-${v.vertical}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="text-sm font-bold text-[#0F172A]">{v.vertical}</p>
                                  {v.teacher_name && <p className="text-[11px] text-[#64748B]">Teacher: {v.teacher_name}</p>}
                                </div>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${v.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{v.status || 'Active'}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-2 mb-2 text-center">
                                <div className="bg-[#F0F7FF] rounded-md py-1"><p className="text-sm font-bold text-[#3B82F6]">{v.classes_purchased}</p><p className="text-[9px] text-[#64748B]">Purchased</p></div>
                                <div className="bg-[#ECFDF5] rounded-md py-1"><p className="text-sm font-bold text-emerald-600">{v.classes_consumed}</p><p className="text-[9px] text-[#64748B]">Consumed</p></div>
                                <div className="bg-[#FFFBEB] rounded-md py-1"><p className="text-sm font-bold text-amber-600">{v.classes_remaining}</p><p className="text-[9px] text-[#64748B]">Remaining</p></div>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${pct}%` }} />
                              </div>
                              {v.amount_received > 0 && (
                                <p className="text-[11px] text-slate-500 mt-2">Paid: <span className="font-bold text-[#0F172A]">{v.currency} {v.amount_received}</span>{v.payment_source ? ` · via ${v.payment_source}` : ''}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Teacher Feedback */}
                  <div className="nb-card p-5" data-testid="parent-hub-feedback">
                    <h3 className="font-bold text-[#0F172A] text-sm mb-3 flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" /> Teacher Feedback</h3>
                    {(parentHubData.recent_feedback || []).length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {parentHubData.recent_feedback.map((fb, i) => (
                          <div key={i} className="p-3 rounded-lg border border-slate-200 bg-[#FFFBEB]/40" data-testid={`feedback-row-${i}`}>
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs font-bold text-[#0F172A]">{fb.teacher_name || 'Teacher'}</p>
                              <span className="text-[10px] text-[#64748B]">{(fb.created_at || '').split('T')[0]}</span>
                            </div>
                            {fb.topic_name && <p className="text-[11px] text-[#64748B] mb-1">Topic: {fb.topic_name}</p>}
                            {fb.rating != null && (
                              <p className="text-[11px] text-amber-600 mb-1 flex items-center gap-1"><Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {fb.rating}/5</p>
                            )}
                            <p className="text-xs text-[#0F172A] leading-relaxed">{fb.feedback || fb.comments || fb.notes || '—'}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#64748B] text-center py-4">No feedback yet from teachers.</p>
                    )}
                  </div>

                  {/* Payment Receipts (upload + status) */}
                  <ReceiptsPanel verticals={user?.verticals || []} />

                  {/* Payment History */}
                  <div className="nb-card p-5" data-testid="parent-hub-payments">
                    <h3 className="font-bold text-[#0F172A] text-sm mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-emerald-600" /> Payment History</h3>
                    {(parentHubData.payments || []).length > 0 ? (
                      <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                        {parentHubData.payments.map((p, i) => (
                          <div key={i} className="py-2.5 flex items-center justify-between" data-testid={`payment-row-${i}`}>
                            <div>
                              <p className="text-xs font-bold text-[#0F172A]">{p.reference || '—'}</p>
                              <p className="text-[11px] text-[#64748B]">{(p.date || '').toString().split('T')[0]} · {p.num_classes || 0} classes</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-bold text-emerald-700">{p.currency} {p.amount || 0}</p>
                              <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${['paid', 'confirmed', 'completed', 'approved'].includes((p.status || '').toLowerCase()) ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{p.status || 'pending'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#64748B] text-center py-4">No payment records yet.</p>
                    )}
                  </div>

                  {/* Performance Insights */}
                  <div className="nb-card p-5">
                    <h3 className="font-bold text-[#0F172A] text-sm mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#3B82F6]" /> Performance Insights</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-[#F8FAFC] rounded-lg p-4 border border-slate-100">
                        <p className="text-xs font-bold text-[#64748B] mb-1">Topics Covered</p>
                        <p className="text-xl font-bold text-[#0F172A]">{parentHubData.topics_covered || 0}</p>
                      </div>
                      <div className="bg-[#F8FAFC] rounded-lg p-4 border border-slate-100">
                        <p className="text-xs font-bold text-[#64748B] mb-1">Average Rating</p>
                        <p className="text-xl font-bold text-[#0F172A] flex items-center gap-1">{parentHubData.avg_rating ? parentHubData.avg_rating.toFixed(1) : 'N/A'} <Star className="w-4 h-4 text-amber-500" /></p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buy More Classes Dialog */}
                <Dialog open={buyClassesOpen} onOpenChange={setBuyClassesOpen}>

                {/* Invoices Section */}
                {parentInvoices.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="parent-invoices">
                    <div className="p-5 border-b border-slate-100"><h3 className="text-base font-bold text-[#0F172A]">Invoices</h3></div>
                    <div className="divide-y divide-slate-100">
                      {parentInvoices.map(inv => (
                        <div key={inv.id} className="p-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><FileText className="w-5 h-5 text-[#1E3A8A]" /></div>
                            <div>
                              <p className="text-sm font-bold text-[#1E3A8A]">{inv.invoice_number}</p>
                              <p className="text-xs text-[#64748B]">{inv.date} &middot; {inv.num_classes} classes &middot; {inv.currency_symbol}{(inv.amount || 0).toLocaleString()}</p>
                            </div>
                          </div>
                          <button onClick={async () => { try { const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: 'blob' }); const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); const a = document.createElement('a'); a.href = url; a.download = `invoice-${inv.invoice_number}.pdf`; a.click(); } catch (err) {  toast.error('Download failed'); } }} className="px-3 py-1.5 bg-[#1E3A8A] text-white rounded-lg text-xs font-bold hover:bg-[#1D4ED8] transition-colors" data-testid={`parent-download-${inv.invoice_number}`}>Download PDF</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                  <DialogContent className="bg-white border border-slate-200 shadow-xl max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-[#0F172A] font-bold">Request More Classes</DialogTitle>
                      <DialogDescription className="text-[#64748B]">Select number of classes for {user?.name}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-3">
                      <div>
                        <Label className="text-xs font-bold text-[#334155]">Student</Label>
                        <Input value={user?.name || ''} disabled className="mt-1 bg-[#F8FAFC]" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold text-[#334155]">Number of Classes</Label>
                        <div className="flex gap-2 mt-1">
                          {[4, 8, 16, 24, 48].map(n => (
                            <button key={n} type="button" onClick={() => setBuyNumClasses(n)}
                              className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${buyNumClasses === n ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-white text-[#334155] border-slate-200 hover:border-[#3B82F6]/50'}`}
                              data-testid={`buy-${n}-classes`}>
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                      <Button onClick={handleBuyClasses} className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-bold h-11 rounded-xl" data-testid="submit-request-btn">
                        Submit Request
                      </Button>
                      <p className="text-[10px] text-[#64748B] text-center">Admin will create an invoice and share it with you</p>
                    </div>
                  </DialogContent>
                </Dialog>
                </>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <Heart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-[#64748B] text-sm">Parent Hub data is not available yet.</p>
                </div>
              )}
            </div>
          )}
        </main>
        </div>
      </div>

      {/* Cancel Dialog */}
      {/* Cancel Class Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancel Class</DialogTitle>
            <DialogDescription>Are you sure you want to cancel this class?</DialogDescription>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-3 mt-3">
              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                <p className="text-sm font-bold text-slate-900">{cancelTarget.topic_name || cancelTarget.course_name || 'Class'}</p>
                <p className="text-sm font-medium text-slate-700">with {cancelTarget.teacher_name}</p>
                <p className="text-xs text-red-600 mt-1">{cancelTarget.date} at {cancelTarget.time || cancelTarget.start_time}</p>
              </div>
              <p className="text-xs text-slate-500">This action cannot be undone. The class will be marked as cancelled.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="flex-1 text-xs" data-testid="keep-class-btn">Keep Class</Button>
                <Button onClick={handleCancelClass} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs" data-testid="confirm-cancel-btn">Yes, Cancel Class</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog - Shows ONLY available teacher slots */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reschedule Class</DialogTitle>
            <DialogDescription>Select an available slot from your teacher's schedule</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            {rescheduleTarget && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-sm font-bold text-slate-900">{rescheduleTarget.topic_name || rescheduleTarget.course_name || 'Class'}</p>
                <p className="text-xs text-slate-600">Current: {rescheduleTarget.date} at {rescheduleTarget.time || rescheduleTarget.start_time}</p>
                {teacherAvailForReschedule?.teacher_name && (
                  <p className="text-xs text-blue-600 mt-1">Teacher: {teacherAvailForReschedule.teacher_name}</p>
                )}
              </div>
            )}

            {/* Available Slots from teacher */}
            {availableSlots.length > 0 ? (
              <div>
                <p className="text-xs font-bold text-slate-700 mb-2">Available Slots</p>
                <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                  {availableSlots.map((slot, idx) => {
                    const isSelected = rescheduleNewDate === slot.date && rescheduleNewTime === slot.start_time;
                    const slotDate = new Date(slot.date + 'T00:00:00');
                    const dayLabel = slotDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                      <button key={idx}
                        onClick={() => {
                          setRescheduleNewDate(slot.date);
                          setRescheduleNewTime(slot.start_time);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center justify-between
                          ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:bg-blue-50'}`}
                        data-testid={`avail-slot-${idx}`}
                      >
                        <div>
                          <span className="font-bold text-sm">{dayLabel}</span>
                          {slot.day_of_week && <span className="ml-2 text-xs opacity-70">({slot.day_of_week})</span>}
                        </div>
                        <div className="text-sm font-semibold">
                          {slot.start_time}{slot.end_time ? ` - ${slot.end_time}` : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 rounded-xl bg-amber-50 border border-amber-200">
                <Calendar className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-bold text-slate-800">No matching available slots</p>
                <p className="text-xs text-slate-600 mt-1">Your teacher has not opened a slot that fits.<br/>Send a request and they'll confirm a new time.</p>
              </div>
            )}

            {/* Request a Slot fallback — routed through the existing scheduler workflow */}
            <div className="border-t border-slate-200 pt-3">
              <button
                type="button"
                onClick={() => {
                  setRescheduleDialogOpen(false);
                  toast.info("Opening the Scheduler — pick a day/time and we'll route it to your teacher.");
                  setTimeout(() => {
                    document.querySelector('[data-testid="dashboard-right-panel"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-dashed border-violet-300 text-violet-700 hover:bg-violet-50 text-sm font-bold transition-all"
                data-testid="request-slot-btn"
              >
                <Sparkles className="w-4 h-4 inline mr-1.5" /> Request a Slot instead
              </button>
              <p className="text-[11px] text-slate-500 mt-1 text-center">Prefer a different day or time? Your teacher will review and confirm.</p>
            </div>
            <Button onClick={handleRescheduleClass} disabled={!rescheduleNewDate || !rescheduleNewTime} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-reschedule-btn">Reschedule Class</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Class Dialog */}
      <Dialog open={addClassOpen} onOpenChange={setAddClassOpen}>
        <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Class</DialogTitle>
            <DialogDescription>Book a new class with your teacher</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            {teacherAvailForReschedule && (
              <div className="text-xs text-slate-600 font-semibold bg-emerald-500/10 rounded-lg p-2 border border-emerald-100">
                <p className="font-medium">Teacher availability loaded</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date</Label><Input type="date" value={addClassDate} onChange={(e) => setAddClassDate(e.target.value)} data-testid="add-class-date" /></div>
              <div><Label className="text-xs">Time</Label><Input type="time" value={addClassTime} onChange={(e) => setAddClassTime(e.target.value)} data-testid="add-class-time" /></div>
            </div>
            <Button onClick={handleAddClass} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-add-class-btn">Add Class</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Recurring Dialog */}
      <Dialog open={changeRecurringOpen} onOpenChange={setChangeRecurringOpen}>
        <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-md">
          <DialogHeader>
            <DialogTitle>Change Recurring Schedule</DialogTitle>
            <DialogDescription>This change requires admin approval</DialogDescription>
          </DialogHeader>
          {changeRecurringTarget && (
            <div className="space-y-4 mt-3">
              <div className="bg-violet-500/10 rounded-lg p-3 border border-violet-400/30 text-xs">
                <p className="font-medium text-violet-800">Current: {(changeRecurringTarget.days_of_week || []).join(', ')} at {changeRecurringTarget.time}</p>
              </div>
              <div>
                <Label className="text-xs">New Days</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {WEEKDAYS.map(day => (
                    <button key={day} onClick={() => setNewRecurringDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${newRecurringDays.includes(day) ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 font-semibold border-slate-300 hover:border-violet-300'}`}
                      data-testid={`recurring-day-${day}`}>{day.slice(0,3)}</button>
                  ))}
                </div>
              </div>
              <div><Label className="text-xs">New Time</Label><Input type="time" value={newRecurringTime} onChange={(e) => setNewRecurringTime(e.target.value)} data-testid="recurring-new-time" /></div>
              <div><Label className="text-xs">Reason</Label><Textarea value={recurringReason} onChange={(e) => setRecurringReason(e.target.value)} placeholder="Why do you want to change?" rows={2} data-testid="recurring-reason" /></div>
              <Button onClick={handleChangeRecurring} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="confirm-recurring-change-btn">Submit for Approval</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
        <DialogContent className="glass-card">
          <DialogHeader>
            <DialogTitle>Rate Your Class</DialogTitle>
            <DialogDescription>{ratingTarget?.course_name || ratingTarget?.subject} - {ratingTarget?.date}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            {RATING_QUESTIONS.map(q => (
              <div key={q.key}>
                <p className="text-sm font-medium text-slate-700 font-semibold mb-1">{q.label}</p>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <button key={n} onClick={() => setClassRatings(prev => ({...prev, [q.key]: n}))}
                      className={`w-7 h-7 rounded text-xs font-medium transition-all ${classRatings[q.key] >= n ? 'bg-amber-500/100 text-white' : 'bg-slate-100 text-slate-600 font-semibold hover:bg-slate-200'}`}
                      data-testid={`rating-${q.key}-${n}`}>{n}</button>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={handleSubmitRating} disabled={submittingRating} className="w-full bg-amber-500/100 hover:bg-amber-600 text-white" data-testid="submit-rating-btn">
              {submittingRating ? 'Submitting...' : 'Submit Rating'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Right utility rail — Scheduler + Leaderboard + Spark (bottom) */}
      <RightUtilityRail sparkMode="student">
        <StudentSchedulerPanel studentVerticals={user?.verticals || []} />
        <LeaderboardSidebar mode="compact" variant="right" />
      </RightUtilityRail>

      {/* Math Olympiad FAB — only when an active challenge exists */}
      {olympiadActive?.challenge && (
        <button
          onClick={() => setOlympiadOpen(true)}
          className="fixed bottom-6 right-44 z-40 hidden sm:flex items-center gap-2 pl-3 pr-4 py-3 rounded-full shadow-lg text-white font-bold text-sm hover:scale-105 transition-transform"
          style={{ background: 'linear-gradient(135deg, #F59E0B, #DC2626)' }}
          title="Weekly Math Olympiad"
          data-testid="olympiad-fab"
        >
          <Trophy className="w-5 h-5" />
          <span>Olympiad</span>
          {!olympiadActive.user_attempt && (
            <span className="ml-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-white animate-pulse" />
          )}
        </button>
      )}
      <MathOlympiadPanel open={olympiadOpen} onClose={() => { setOlympiadOpen(false); fetchOlympiad(); }} />
    </div>
  );
};

export default StudentDashboard;
